import {
  IBalanceHistoryResult,
  IBalanceResponse,
  IBalanceResult,
} from "@/core/models/balance.model";

const balanceCompositionTransformer = (
  data: IBalanceResponse
): IBalanceResult["composition"] => ({
  co: {
    topGas: data.output.top_gas_co,
    tgScrubbed: data.output.tg_scrubbed_co,
    reformedGas: data.output.reformed_gas_co,
    bustleGas: data.output.bustle_gas_co,
    flueGas: data.output.flue_gas_co,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_co,
  },
  co2: {
    topGas: data.output.top_gas_co2,
    tgScrubbed: data.output.tg_scrubbed_co2,
    reformedGas: data.output.reformed_gas_co2,
    bustleGas: data.output.bustle_gas_co2,
    flueGas: data.output.flue_gas_co2,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_co2,
  },
  h2: {
    topGas: data.output.top_gas_h2,
    tgScrubbed: data.output.tg_scrubbed_h2,
    reformedGas: data.output.reformed_gas_h2,
    bustleGas: data.output.bustle_gas_h2,
    flueGas: data.output.flue_gas_h2,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_h2,
  },
  h2o: {
    topGas: data.output.top_gas_h2o,
    tgScrubbed: data.output.tg_scrubbed_h2o,
    reformedGas: data.output.reformed_gas_h2o,
    bustleGas: data.output.bustle_gas_h2o,
    flueGas: data.output.flue_gas_h2o,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_h2o,
  },
  n2: {
    topGas: data.output.top_gas_n2,
    tgScrubbed: data.output.tg_scrubbed_n2,
    reformedGas: data.output.reformed_gas_n2,
    bustleGas: data.output.bustle_gas_n2,
    flueGas: data.output.flue_gas_n2,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_n2,
  },
  ch4: {
    topGas: data.output.top_gas_ch4,
    tgScrubbed: data.output.tg_scrubbed_ch4,
    reformedGas: data.output.reformed_gas_ch4,
    bustleGas: data.output.bustle_gas_ch4,
    flueGas: data.output.flue_gas_ch4,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_ch4,
  },
  c2h6: {
    topGas: data.output.top_gas_c2h6,
    tgScrubbed: data.output.tg_scrubbed_c2h6,
    reformedGas: data.output.reformed_gas_c2h6,
    bustleGas: data.output.bustle_gas_c2h6,
    flueGas: data.output.flue_gas_c2h6,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_c2h6,
  },
  c3h8: {
    topGas: data.output.top_gas_c3h8,
    tgScrubbed: data.output.tg_scrubbed_c3h8,
    reformedGas: data.output.reformed_gas_c3h8,
    bustleGas: data.output.bustle_gas_c3h8,
    flueGas: data.output.flue_gas_c3h8,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_c3h8,
  },
  c4h10: {
    topGas: data.output.top_gas_c4h10,
    tgScrubbed: data.output.tg_scrubbed_c4h10,
    reformedGas: data.output.reformed_gas_c4h10,
    bustleGas: data.output.bustle_gas_c4h10,
    flueGas: data.output.flue_gas_c4h10,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_c4h10,
  },
  c5h12: {
    topGas: data.output.top_gas_c5h12,
    tgScrubbed: data.output.tg_scrubbed_c5h12,
    reformedGas: data.output.reformed_gas_c5h12,
    bustleGas: data.output.bustle_gas_c5h12,
    flueGas: data.output.flue_gas_c5h12,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_c5h12,
  },
  c6h14: {
    topGas: data.output.top_gas_c6h14,
    tgScrubbed: data.output.tg_scrubbed_c6h14,
    reformedGas: data.output.reformed_gas_c6h14,
    bustleGas: data.output.bustle_gas_c6h14,
    flueGas: data.output.flue_gas_c6h14,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_c6h14,
  },
  o2: {
    topGas: data.output.top_gas_o2,
    tgScrubbed: data.output.tg_scrubbed_o2,
    reformedGas: data.output.reformed_gas_o2,
    bustleGas: data.output.bustle_gas_o2,
    flueGas: data.output.flue_gas_o2,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_o2,
  },
  flow: {
    topGas: data.output.top_gas_flow,
    tgScrubbed: data.output.tg_scrubbed_flow,
    reformedGas: data.output.reformed_gas_flow,
    bustleGas: data.output.bustle_gas_flow,
    flueGas: data.output.flue_gas_flow,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_flow,
    streamToFeedMixer: data.output.added_steam_feedmixed,
  },
  temp: {
    bustleGas: data.output.bustle_gas_temp,
    feedGasAfterPGMixer: data.output.feed_gas_after_pg_temp,
  },
});

const balanceReformerTransformer = (
  data: IBalanceResponse
): IBalanceResult["reformer"] => ({
  sensible_heat_to_tubes: data.output.sensible_heat_to_tubes,
  reaction_heat_to_tubes: data.output.reaction_heat_to_tubes,
  reformer_heat_losses: data.output.reformer_heat_losses,
  total_heat_released_by_fuel: data.output.total_heat_released_by_fuel,
});

const balanceRecuperatorTransformer = (
  data: IBalanceResponse
): IBalanceResult["recuperator"] => ({
  hot_air_bundle: data.output.hot_air_bundle,
  cold_air_bundle: data.output.cold_air_bundle,
  feed_gas: data.output.feed_gas,
  total_recovered: data.output.total_recovered,
  lost_to_stack: data.output.lost_to_stack,
  lost_to_seal: data.output.lost_to_seal,
});

export const balanceResultTransformer = (
  response: IBalanceResponse
): IBalanceResult => ({
  composition: balanceCompositionTransformer(response),
  product: [response.output.A, response.output.B, response.output.C],
  reformer: balanceReformerTransformer(response),
  recuperator: balanceRecuperatorTransformer(response),
});

export const balanceHistoryTransformer = (
  response: IBalanceResponse[]
): IBalanceHistoryResult[] => {
  return response.map((balance) => ({
    id: balance.id,
    time: balance.created_at,
    ...balanceResultTransformer(balance),
  }));
};
